import React, { useCallback, useEffect, useRef } from "react";
import { bool, string } from "prop-types";
import queryString from "qs";

import Drawer from "src/shared/components/DrawerModal";
import { useFeatureFlags } from "src/shared/components/FeatureFlags";
import StickyButtons from "src/shared/contact-boxes/components/StickyButtons";
import ExpertsForm from "src/shared/contact-boxes/experts/components/ExpertsForm";
import SidePanelForm from "src/shared/contact-boxes/experts/components/SidePanelForm";
import { defaultPostSubmitAnswers } from "src/shared/contact-boxes/experts/constants/defaultPostSubmitAnswers";
import { ASK_QUESTION } from "src/shared/contact-boxes/experts/constants/formTypes";
import analytics from "src/shared/contact-boxes/experts/utilities/analytics";
import { withDrawerContextProvider } from "src/shared/contact-boxes/experts/utilities/drawerHelpers";
import useActiveExperts from "src/shared/contact-boxes/experts/utilities/hooks/useActiveExperts";
import useFeatureState from "src/shared/contact-boxes/experts/utilities/hooks/useFeatureState";
import useUpdatePostSubmitQuestionnaire from "src/shared/contact-boxes/experts/utilities/hooks/useUpdatePostSubmitQuestionnaire";
import useUserProfileQuery from "src/shared/contact-boxes/experts/utilities/hooks/useUserProfileQuery";
import useReducerActions from "src/shared/contact-boxes/experts/utilities/useReducerActions";
import useIsMobileDeviceSize from "src/shared/utilities/state/apollo/hooks/useIsMobileDeviceSize";
import {
  useSticky,
  withStickyContextProvider
} from "src/shared/utilities/stickyHelpers";
import useAbTests from "src/shared/utilities/useAbTests";

import { RESTRICTED_SALE } from "../../constants/saleTypes";
import MobileForm from "../MobileForm";
import WhatYouShouldKnow from "../WhatYouShouldKnow";
import ExpertContactButtons from "./ExpertContactButtons";
import * as S from "./styled";

/**
 * TODO fossa_1638_experts_contact_box_refactor cleanup: get rid of isMobile
 * Instead use isMobileDeviceSize wherever needed
 * */
const DrawerForm = ({ className, isMobile }) => {
  const { flags } = useFeatureFlags(
    ["fossa_1638_experts_contact_box_refactor"],
    true
  );
  const {
    fossa_1638_experts_contact_box_refactor: abTestVariant,
    loading: abTestsLoading
  } = useAbTests("fossa_1638_experts_contact_box_refactor");
  const isMobileDeviceSize = useIsMobileDeviceSize();
  const {
    expertsApiUuid,
    isDrawerOpen,
    listingId,
    modalAgent
  } = useFeatureState();
  const [stickyVisible, setStickyVisible] = useSticky(isMobile);
  const {
    dispatchResetContactFormType,
    dispatchResetMessageScreeningFlag,
    dispatchResetPostSubmitAnswers,
    dispatchSetContactFormType,
    dispatchSetPostSubmitAnswers,
    dispatchSetWhatYouShouldKnowModal
  } = useReducerActions();
  const { data, listing, loading: activeExpertsLoading } = useActiveExperts();
  const {
    loading: userProfileLoading,
    showPostSubmitQuestionnaire
  } = useUserProfileQuery();
  const updatePostSubmitQuestionnaire = useUpdatePostSubmitQuestionnaire();
  const formikRef = useRef(null);

  const openContactForm = useCallback(
    type => {
      analytics({
        action: `click_to_${type}`,
        event_label: `${
          stickyVisible ? "sticky" : "inline"
        }|uuid=${expertsApiUuid}`,
        isHdp: Boolean(listingId)
      });

      if (listing?.is_land_lease || listing?.sale_type === RESTRICTED_SALE) {
        dispatchSetWhatYouShouldKnowModal({
          contactFormType: type,
          isModalOpen: true
        });
      } else {
        dispatchSetContactFormType(type);
      }

      if (stickyVisible) {
        setStickyVisible(false);
      }
    },
    [listingId, expertsApiUuid, stickyVisible, listing]
  );

  useEffect(() => {
    const { search } = window.location;
    const { contact_drawer_open: contactDrawerOpen } = queryString.parse(
      search,
      { ignoreQueryPrefix: true }
    );

    if (contactDrawerOpen) {
      openContactForm(ASK_QUESTION);
    }
  }, []);

  const anyLoading = () =>
    !flags || abTestsLoading || activeExpertsLoading || userProfileLoading;

  if (!data || anyLoading()) {
    return null;
  }

  const showNewVariant =
    flags.fossa_1638_experts_contact_box_refactor && abTestVariant === "new";

  const handleContentClose = () => {
    dispatchResetContactFormType();
    dispatchResetMessageScreeningFlag();
    dispatchResetPostSubmitAnswers();
  };

  const handleManualContentClose = async () => {
    if (showPostSubmitQuestionnaire) {
      const postSubmitAnswers = Object.assign(
        ...Object.values(defaultPostSubmitAnswers)
      );

      dispatchSetPostSubmitAnswers(postSubmitAnswers);
      await updatePostSubmitQuestionnaire(postSubmitAnswers);

      if (!showNewVariant && formikRef.current?.submitForm) {
        formikRef.current.submitForm();
        formikRef.current = null;
      }
    }

    handleContentClose();
  };

  return (
    <>
      <S.ButtonBox
        className={className}
        // TODO FOS-1638: do we still need two attributes for specs?
        data-qa={`${isMobile ? "mobile" : "desktop"}-expert-button-box`}
        isMobile={isMobile}
      >
        <StickyButtons isMobile={isMobile} listing={listing}>
          <ExpertContactButtons
            isVertical={false}
            openContactForm={openContactForm}
          />
        </StickyButtons>
        <ExpertContactButtons isVertical openContactForm={openContactForm}>
          {Boolean(listing) && <WhatYouShouldKnow listing={listing} />}
        </ExpertContactButtons>
      </S.ButtonBox>
      {showNewVariant ? (
        <SidePanelForm
          isOpen={isDrawerOpen}
          onClose={handleManualContentClose}
          onSubmit={handleContentClose}
        />
      ) : (
        <>
          {!isMobile ? (
            <Drawer
              data-qa="desktop_drawer"
              disableClickOutside={Boolean(modalAgent)}
              handleOnClose={handleManualContentClose}
              isOpen={Boolean(isDrawerOpen && !isMobileDeviceSize)}
              render={() => (
                <ExpertsForm
                  className={className}
                  innerRef={formikRef}
                  onCancel={handleManualContentClose}
                  onSubmit={handleContentClose}
                />
              )}
              rootElementId="#content"
            />
          ) : (
            <MobileForm
              data-qa="mobile_drawer"
              innerRef={formikRef}
              isOpen={Boolean(isDrawerOpen && isMobileDeviceSize)}
              onClose={handleManualContentClose}
              onSubmit={handleContentClose}
            />
          )}
        </>
      )}
    </>
  );
};

DrawerForm.propTypes = {
  className: string,
  isMobile: bool
};

DrawerForm.defaultProps = {
  className: "",
  isMobile: false
};

// TODO fossa_1638_experts_contact_box_refactor cleanup: get rid of DrawerContext
export default withDrawerContextProvider(
  withStickyContextProvider(DrawerForm),
  true
);
